import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';

function TestPage() {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [stampList, setStampList] = useState([]);
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [examList, setExamList] = useState([]);
  const [problemData, setProblemData] = useState([]);
  const [allProblemsLoading, setAllProblemsLoading] = useState(false);
  const [addressFilter, setAddressFilter] = useState('');
  const [filteredProblems, setFilteredProblems] = useState([]);
  
  // 페이지네이션 관련 상태 추가
  const [currentPage, setCurrentPage] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const studentsPerPage = 50; // API에서 사용하는 페이지당 학생 수

  // 난이도(step)에 따른 문제 레이팅 매핑
  const STEP_TO_RATING = {
    1: 1000,
    2: 3000,
    3: 5000,
    4: 7000,
    5: 9000
  };

  // 레이팅 계산 상수
  const K = 160;  // K 값을 160으로 수정 (기존 32에서 변경)
  const Q = 2000;
  const INITIAL_RATING = 1000;

  // 레이팅 계산 함수
  const calculateRating = (problemData) => {
    if (!problemData || problemData.length === 0) return problemData;

    let currentRating = INITIAL_RATING; // 초기 레이팅
    const ratedProblems = [];

    for (const problem of problemData) {
      // 문제의 레이팅 설정 (step에 따라)
      const problemRating = STEP_TO_RATING[problem.step] || STEP_TO_RATING[1];
      
      const ratingBefore = currentRating; // Pp: 풀이 전 레이팅
      
      // W값 설정: 정답(⭕)이면 W=1, 오답(❌)이면 W=0
      const resultIcon = renderResultIcon(problem.result);
      
      // API에서 제공하는 result 값을 사용하여 W 값 설정 (true: 맞음, false: 틀림)
      const W = problem.result === true ? 1 : 0;
      
      // 기대 승률 계산: 1 / (10^((P_op - P_me)/Q) + 1)
      const expectedScore = 1 / (Math.pow(10, (problemRating - currentRating) / Q) + 1);
      
      // 새 레이팅 계산: Pp + K(W - 기대 승률)
      const newRating = Math.round(ratingBefore + K * (W - expectedScore));
      
      ratedProblems.push({
        ...problem,
        problem_rating: problemRating,
        rating_before: ratingBefore,
        rating_after: newRating,
        rating_change: newRating - ratingBefore,
        expected_score: (expectedScore * 100).toFixed(0) + '%', // 백분율로 표시
        w_value: W // W 값 추가
      });
      
      currentRating = newRating; // 다음 문제를 위해 현재 레이팅 업데이트
    }
    
    return ratedProblems;
  };

  // 학생 목록 가져오기
  const fetchStudents = async (page = 1) => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users/student?per_page=${studentsPerPage}&page=${page}`, {
        headers: {
          'Authorization': token
        }
      });

      if (!response.ok) {
        throw new Error('학생 목록을 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      setStudents(result.data);
      
      // 페이지네이션을 위한 메타 정보 처리
      if (result.meta) {
        console.log('API 메타 정보:', result.meta); // 디버깅용 로그 추가
        setTotalStudents(result.meta.total || 0);
        setTotalPages(Math.ceil((result.meta.total || 0) / studentsPerPage));
      } else if (result.data && Array.isArray(result.data)) {
        // 메타 정보가 없는 경우 데이터 길이로 추정
        console.log('메타 정보 없음, 데이터 길이로 처리:', result.data.length);
        const estimatedTotal = Math.max(200, result.data.length * 4); // 최소 200명으로 가정
        setTotalStudents(estimatedTotal);
        setTotalPages(Math.ceil(estimatedTotal / studentsPerPage));
      }
    } catch (error) {
      console.error('학생 목록 조회 오류:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 페이지 변경 핸들러
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchStudents(pageNumber);
  };

  // 스탬프 목록 가져오기
  const fetchStampList = async (studentId) => {
    if (!studentId) return;
    
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams?student_id=${studentId}`, {
        headers: {
          'Authorization': token
        }
      });

      if (response.status === 204) {
        setStampList([]);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setStampList(data.data.stamps);
    } catch (error) {
      console.error('스탬프 목록 조회 오류:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 시험 목록 가져오기
  const fetchExamList = async (stampId) => {
    if (!stampId) return;
    
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}`, {
        headers: { 'Authorization': token }
      });

      if (response.status === 204) {
        setExamList([]);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // 시험을 생성 날짜순으로 정렬 (최신 시험이 나중에 오도록)
      const sortedExams = [...data.data].sort((a, b) => 
        new Date(a.created_at || a.exam_created_at) - new Date(b.created_at || b.exam_created_at)
      );
      setExamList(sortedExams);
      
      // 모든 시험의 문제 정보를 가져옴
      fetchAllExamProblems(stampId, sortedExams);
    } catch (error) {
      console.error('시험 목록 조회 오류:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 모든 시험의 문제 정보 가져오기
  const fetchAllExamProblems = async (stampId, exams) => {
    if (!stampId || !exams || exams.length === 0) return;
    
    setAllProblemsLoading(true);
    setProblemData([]);
    
    try {
      const token = await getAccessToken();
      let allProblems = [];
      
      // 각 시험의 문제 정보를 순차적으로 가져옴
      for (const exam of exams) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${exam.exam_id}`, {
          headers: { 'Authorization': token }
        });

        if (!response.ok) {
          console.error(`시험 ID ${exam.exam_id}의 문제를 불러오는데 실패했습니다.`);
          continue;
        }

        const data = await response.json();
        
        // 문제 정보 및 결과 데이터 처리
        const problems = data.data.problems_list || [];
        const results = data.data.result || [];
        
        // 결과 맵 생성
        const resultMap = new Map(
          results.map(item => [item.problem_id, item.result])
        );
        
        // 문제 정보 가공
        const processedProblems = problems.map((problem, index) => ({
          problem_id: problem.problem_id,
          address: problem.address || '정보 없음', // 단원 정보
          step: problem.step || 0, // 난이도
          result: resultMap.get(problem.problem_id) || null, // 정오답 여부
          created_at: problem.created_at || new Date().toISOString(), // 시간 정보
          exam_id: exam.exam_id,
          exam_count: exam.exam_count,
          exam_type: exam.exam_type,
          exam_created_at: exam.created_at || exam.exam_created_at,
          problem_index: index + 1 // 문제 순서
        }));
        
        allProblems = [...allProblems, ...processedProblems];
      }
      
      // 시험 생성 시간 순, 문제 순서대로 정렬
      allProblems.sort((a, b) => {
        const examDateA = new Date(a.exam_created_at);
        const examDateB = new Date(b.exam_created_at);
        
        // 우선 시험 생성 시간으로 비교
        if (examDateA.getTime() !== examDateB.getTime()) {
          return examDateA - examDateB;
        }
        
        // 시험 시간이 같다면 문제 순서로 비교
        return a.problem_index - b.problem_index;
      });
      
      // 레이팅 계산 적용
      const ratedProblems = calculateRating(allProblems);
      setProblemData(ratedProblems);
    } catch (error) {
      console.error('시험 문제 정보 조회 오류:', error);
    } finally {
      setAllProblemsLoading(false);
    }
  };

  // 시험의 문제 정보 가져오기 (단일 시험용 - 필요할 경우 사용)
  const fetchExamProblems = async (stampId, examId) => {
    if (!stampId || !examId) return;
    
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}`, {
        headers: { 'Authorization': token }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // 문제 정보 및 결과 데이터 처리
      const problems = data.data.problems_list || [];
      const results = data.data.result || [];
      
      // 결과 맵 생성
      const resultMap = new Map(
        results.map(item => [item.problem_id, item.result])
      );
      
      // 현재 시험 정보 찾기
      const currentExam = examList.find(e => e.exam_id === examId) || { 
        exam_count: '알 수 없음', 
        exam_type: '알 수 없음',
        created_at: new Date().toISOString()
      };
      
      // 문제 정보 가공
      const processedProblems = problems.map((problem, index) => ({
        problem_id: problem.problem_id,
        address: problem.address || '정보 없음', // 단원 정보
        step: problem.step || 0, // 난이도
        result: resultMap.get(problem.problem_id) || null, // 정오답 여부
        created_at: problem.created_at || new Date().toISOString(), // 시간 정보
        exam_id: examId,
        exam_count: currentExam.exam_count,
        exam_type: currentExam.exam_type,
        exam_created_at: currentExam.created_at || currentExam.exam_created_at,
        problem_index: index + 1 // 문제 순서
      }));
      
      // 레이팅 계산 적용
      const ratedProblems = calculateRating(processedProblems);
      setProblemData(ratedProblems);
    } catch (error) {
      console.error('시험 문제 정보 조회 오류:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 단원 정보로 문제 필터링 및 레이팅 재계산
  const filterProblemsByAddress = () => {
    if (!problemData || problemData.length === 0) return;
    
    // 필터가 비어있으면 모든 문제 표시
    if (!addressFilter.trim()) {
      setFilteredProblems(problemData);
      return;
    }
    
    // 단원정보로 문제 필터링 - 필터값 이상인 단원만 제외하도록 변경
    const filtered = problemData.filter(problem => {
      // 단원 정보가 없는 경우
      if (!problem.address || problem.address === '정보 없음') {
        return true; // 기본적으로 포함
      }
      
      // 단원 주소 비교를 위한 준비
      const filterParts = addressFilter.trim().split('/');
      const addressParts = problem.address.split('/');
      
      // 부분별 비교
      for (let i = 0; i < filterParts.length; i++) {
        // 문제 주소에 해당 깊이가 없는 경우
        if (i >= addressParts.length) {
          return true; // 필터보다 짧은 주소는 포함
        }
        
        // 숫자로 변환하여 비교 (숫자가 아닌 경우 원본 문자열 사용)
        const filterPartNum = parseInt(filterParts[i]);
        const addressPartNum = parseInt(addressParts[i]);
        
        // 숫자로 변환 가능한 경우 숫자 비교
        if (!isNaN(filterPartNum) && !isNaN(addressPartNum)) {
          if (addressPartNum > filterPartNum) {
            return false; // 현재 부분이 필터보다 크면 제외
          } else if (addressPartNum < filterPartNum) {
            return true; // 현재 부분이 필터보다 작으면 포함
          }
          // 같은 경우 다음 부분 비교
        } else {
          // 문자열 비교
          if (addressParts[i] > filterParts[i]) {
            return false; // 현재 부분이 필터보다 크면 제외
          } else if (addressParts[i] < filterParts[i]) {
            return true; // 현재 부분이 필터보다 작으면 포함
          }
          // 같은 경우 다음 부분 비교
        }
      }
      
      // 모든 부분이 동일하면 포함 (필터와 정확히 일치)
      return true;
    });
    
    // 필터링된 문제만으로 레이팅 다시 계산
    let currentRating = INITIAL_RATING;
    const recalculated = filtered.map(problem => {
      const problemRating = STEP_TO_RATING[problem.step] || STEP_TO_RATING[1];
      const ratingBefore = currentRating;
      
      // W값 설정
      const W = problem.result === true ? 1 : 0;
      
      // 기대 승률 계산
      const expectedScore = 1 / (Math.pow(10, (problemRating - currentRating) / Q) + 1);
      
      // 새 레이팅 계산
      const newRating = Math.round(ratingBefore + K * (W - expectedScore));
      
      currentRating = newRating;
      
      return {
        ...problem,
        rating_before: ratingBefore,
        rating_after: newRating,
        rating_change: newRating - ratingBefore,
        expected_score: (expectedScore * 100).toFixed(0) + '%'
      };
    });
    
    setFilteredProblems(recalculated);
  };
  
  // 단원 필터 변경 시 필터링 적용
  useEffect(() => {
    filterProblemsByAddress();
  }, [addressFilter, problemData]);

  useEffect(() => {
    // 1페이지로 시작하고, totalPages를 최소 4로 설정 (임시)
    setCurrentPage(1);
    setTotalPages(Math.max(4, totalPages));
    fetchStudents(1);
  }, []);

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setSelectedStamp(null);
    setExamList([]);
    setProblemData([]);
    fetchStampList(student.student_id);
  };

  const handleStampSelect = (stamp) => {
    setSelectedStamp(stamp);
    setProblemData([]);
    fetchExamList(stamp.stamp_id);
  };

  const handleExamSelect = (examId, stampId) => {
    fetchExamProblems(stampId, examId);
  };

  const renderResultIcon = (result) => {
    if (result === null) return '❌'; // 미응시도 오답으로 처리
    if (result === true) return '⭕'; // 맞음 
    if (result === false) return '❌'; // 틀림
    return result;
  };

  // 레이팅 변화에 따른 클래스 반환
  const getRatingChangeClass = (change) => {
    if (change > 0) return 'text-green-600 font-bold';
    if (change < 0) return 'text-red-600 font-bold';
    return 'text-gray-600';
  };

  // 레이팅 변화 표시
  const renderRatingChange = (change) => {
    if (change === 0) return "-";
    if (change > 0) return `▲${change}`;
    return `▼${Math.abs(change)}`;
  };

  // 페이지네이션 컴포넌트
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    // 최소 4페이지는 보여줌 (임시 설정)
    const effectiveTotalPages = Math.max(4, totalPages);
    
    const pageNumbers = [];
    
    // 페이지 번호 계산 (최대 5개의 페이지 번호만 표시)
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(effectiveTotalPages, startPage + 4);
    
    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return (
      <div className="mt-4 flex justify-center">
        <nav className="inline-flex rounded-3xl shadow">
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center rounded-l-md px-4 py-2 text-sm font-medium ${
              currentPage === 1
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } border border-r-0 border-gray-300`}
          >
            처음
          </button>
          
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
              currentPage === 1
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } border border-r-0 border-gray-300`}
          >
            이전
          </button>
          
          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => onPageChange(number)}
              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                currentPage === number
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              } border border-r-0 border-gray-300`}
            >
              {number}
            </button>
          ))}
          
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === effectiveTotalPages}
            className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
              currentPage === effectiveTotalPages
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } border border-r-0 border-gray-300`}
          >
            다음
          </button>
          
          <button
            onClick={() => onPageChange(effectiveTotalPages)}
            disabled={currentPage === effectiveTotalPages}
            className={`relative inline-flex items-center rounded-r-md px-4 py-2 text-sm font-medium ${
              currentPage === effectiveTotalPages
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            } border border-gray-300`}
          >
            마지막
          </button>
        </nav>
      </div>
    );
  };

  return (
    <Layout>
      <div className="page-container">
        <h1 className="mb-6 text-3xl font-bold">테스트 페이지</h1>
        
        {/* 학생 선택 섹션 */}
        <div className="mb-6 rounded-3xl bg-white p-6 shadow">
          <h2 className="mb-4 text-xl font-bold">학생 목록</h2>
          <div className="mb-3">
            <span className="text-sm text-gray-600">
              전체 {totalStudents}명 중 {(currentPage - 1) * studentsPerPage + 1} - {Math.min(currentPage * studentsPerPage, totalStudents)}명 표시
            </span>
          </div>
          {isLoading && !students.length ? (
            <p>학생 정보를 불러오는 중...</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {students.map((student) => (
                <div
                  key={student.student_id}
                  className={`cursor-pointer rounded-3xl border p-4 hover:bg-gray-50 ${
                    selectedStudent?.student_id === student.student_id ? 'border-green-500 bg-green-50' : ''
                  }`}
                  onClick={() => handleStudentSelect(student)}
                >
                  <p className="font-semibold">{student.student_name}</p>
                  <p className="text-sm text-gray-500">{student.school_name} {student.school_grade}</p>
                </div>
              ))}
            </div>
          )}
          
          {/* 페이지네이션 추가 */}
          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={handlePageChange} 
          />
        </div>
        
        {/* 스탬프 선택 섹션 */}
        {selectedStudent && (
          <div className="mb-6 rounded-3xl bg-white p-6 shadow">
            <h2 className="mb-4 text-xl font-bold">교육과정 목록</h2>
            {isLoading && !stampList.length ? (
              <p>교육과정 정보를 불러오는 중...</p>
            ) : stampList.length === 0 ? (
              <p>교육과정이 없습니다.</p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {stampList.map((stamp) => (
                  <div
                    key={stamp.stamp_id}
                    className={`cursor-pointer rounded-3xl border p-4 hover:bg-gray-50 ${
                      selectedStamp?.stamp_id === stamp.stamp_id ? 'border-green-500 bg-green-50' : ''
                    }`}
                    onClick={() => handleStampSelect(stamp)}
                  >
                    <p className="font-semibold">{stamp.tree_name}</p>
                    <p className="text-sm text-gray-500">교육과정: {stamp.edu_ver}</p>
                    <p className="text-sm text-gray-500">테스트 횟수: {stamp.exam_count}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* 시험 목록 섹션 */}
        {selectedStamp && (
          <div className="mb-6 rounded-3xl bg-white p-6 shadow">
            <h2 className="mb-4 text-xl font-bold">시험 목록</h2>
            {isLoading && !examList.length ? (
              <p>시험 정보를 불러오는 중...</p>
            ) : examList.length === 0 ? (
              <p>시험이 없습니다.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">시험 ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">회차</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">유형</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">문제 수</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">평균 난이도</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">상태</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">생성일</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">액션</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {examList.map((exam) => (
                      <tr key={exam.exam_id}>
                        <td className="whitespace-nowrap px-6 py-4">{exam.exam_id}</td>
                        <td className="whitespace-nowrap px-6 py-4">{exam.exam_count}</td>
                        <td className="whitespace-nowrap px-6 py-4">{exam.exam_type}</td>
                        <td className="whitespace-nowrap px-6 py-4">{exam.total_problems}</td>
                        <td className="whitespace-nowrap px-6 py-4">{exam.avg_difficulty}</td>
                        <td className="whitespace-nowrap px-6 py-4">
                          {exam.exam_status === 999 ? "결과확정완료" : 
                           exam.exam_status === 400 ? "결과입력완료" : 
                           exam.exam_status === 200 ? "결과입력필요" : "상태 미정"}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          {new Date(exam.created_at || exam.exam_created_at).toLocaleDateString()}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <button
                            onClick={() => handleExamSelect(exam.exam_id, selectedStamp.stamp_id)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            문제 보기
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {/* 문제 정보 섹션 */}
        {selectedStamp && (
          <div className="rounded-3xl bg-white p-6 shadow">
            <h2 className="mb-4 text-xl font-bold">모든 시험 문제 정보</h2>
            
            {/* 단원 정보 필터 추가 */}
            <div className="mb-4">
              <label htmlFor="addressFilter" className="mb-2 block text-sm font-medium text-gray-700">
                단원 정보 필터 (예: 16/1/1)
              </label>
              <div className="flex">
                <input
                  type="text"
                  id="addressFilter"
                  className="block w-full max-w-md rounded-3xl border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="필터링할 단원 정보 입력"
                  value={addressFilter}
                  onChange={(e) => setAddressFilter(e.target.value)}
                />
                <button
                  className="ml-2 rounded-3xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  onClick={filterProblemsByAddress}
                >
                  필터 적용
                </button>
                <button
                  className="ml-2 rounded-3xl bg-gray-600 px-4 py-2 text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  onClick={() => setAddressFilter('')}
                >
                  필터 초기화
                </button>
              </div>
            </div>
            
            {allProblemsLoading ? (
              <p>모든 시험의 문제 정보를 불러오는 중...</p>
            ) : problemData.length === 0 ? (
              <p>문제 정보가 없습니다.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">순서</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">시험 ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">회차</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">유형</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">문제 번호</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">문제 ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">단원 정보</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">난이도</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">정오답</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">문제 레이팅</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">풀이 전 레이팅</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">기대 승률</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">레이팅 변화</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">누적 레이팅</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">W 값</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {(filteredProblems.length > 0 ? filteredProblems : problemData).map((problem, globalIndex) => (
                      <tr key={`${problem.exam_id}-${problem.problem_id}`}>
                        <td className="whitespace-nowrap px-6 py-4">{globalIndex + 1}</td>
                        <td className="whitespace-nowrap px-6 py-4">{problem.exam_id}</td>
                        <td className="whitespace-nowrap px-6 py-4">{problem.exam_count}</td>
                        <td className="whitespace-nowrap px-6 py-4">{problem.exam_type}</td>
                        <td className="whitespace-nowrap px-6 py-4">{problem.problem_index}</td>
                        <td className="whitespace-nowrap px-6 py-4">{problem.problem_id}</td>
                        <td className="whitespace-nowrap px-6 py-4">{problem.address}</td>
                        <td className="whitespace-nowrap px-6 py-4">{problem.step}</td>
                        <td className="whitespace-nowrap px-6 py-4">
                          {renderResultIcon(problem.result)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          {problem.problem_rating}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          {problem.rating_before}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          {problem.expected_score}
                        </td>
                        <td className={`whitespace-nowrap px-6 py-4 ${getRatingChangeClass(problem.rating_change)}`}>
                          {renderRatingChange(problem.rating_change)}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 font-bold">
                          {problem.rating_after}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          {problem.w_value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TestPage; 